import { Fragment, memo } from "react";

import { Box, TableCell } from "@mui/material";

import { HorizToggleChevron } from "../../buttons";
import { Checkbox } from "../../input";
import { BODY_CELL_SX, LEADING_COL_WIDTH, RowDivider, StyledTableRow, colFlexAlign, getCellInnerSx } from "../cell";
import { RowCheckboxState } from "../useSelection";
import { TableColumn, TableRow } from "../types";

interface GroupRowProps<TRow> {
    row: TableRow<TRow>;
    rowIndex: number;
    columns: TableColumn<TRow>[];
    /** Pre-computed by useSelection. Present iff selection is enabled. */
    checkbox?: RowCheckboxState;
    isExpanded: boolean;
    setIsExpanded: (expanded: boolean) => void;
    divider: string;
    /** The expanded block rendered below this row. Caller composes <ItemRows ...>. */
    itemBlock: React.ReactNode;
}

function GroupRowImpl<TRow>({
    row,
    rowIndex,
    columns,
    checkbox,
    isExpanded,
    setIsExpanded,
    divider,
    itemBlock
}: GroupRowProps<TRow>) {
    const hasCheckbox = !!checkbox;
    const leadingCells = hasCheckbox ? 2 : 1;
    const totalColSpan = columns.length + leadingCells;

    return (
        <Fragment>
            <StyledTableRow
                onClick={(e) => {
                    // Ignore clicks that land on interactive elements (CTA buttons, links, inputs) so they
                    // don't bubble up and toggle the row. The chevron + checkbox stop propagation themselves.
                    if ((e.target as HTMLElement).closest("button, a, input, [role='button']")) return;
                    setIsExpanded(!isExpanded);
                }}
                sx={{ position: "relative", cursor: "pointer" }}
            >
                {hasCheckbox && (
                    <TableCell sx={{ ...BODY_CELL_SX, width: LEADING_COL_WIDTH }}>
                        <RowDivider color={divider} />
                        <Box
                            sx={{
                                ...getCellInnerSx({
                                    colIdx: 0,
                                    totalColSpan,
                                    isGrouped: true,
                                    hasCheckbox
                                }),
                                gap: 0
                            }}
                        >
                            <span onClick={(e) => e.stopPropagation()}>
                                <Checkbox
                                    checked={checkbox.checked}
                                    indeterminate={checkbox.indeterminate}
                                    onChange={checkbox.onToggle}
                                />
                            </span>
                        </Box>
                    </TableCell>
                )}
                <TableCell sx={{ ...BODY_CELL_SX, width: LEADING_COL_WIDTH }}>
                    {!hasCheckbox && <RowDivider color={divider} />}
                    <Box
                        sx={{
                            ...getCellInnerSx({
                                colIdx: leadingCells - 1,
                                totalColSpan,
                                isGrouped: true,
                                hasCheckbox
                            }),
                            gap: 0
                        }}
                    >
                        <HorizToggleChevron
                            expanded={isExpanded}
                            onClick={(e) => {
                                e.stopPropagation();
                                setIsExpanded(!isExpanded);
                            }}
                        />
                    </Box>
                </TableCell>
                {columns.map((col, j) => {
                    const colIdx = j + leadingCells;
                    return (
                        <TableCell
                            key={col.dataIndex}
                            align={col.align ?? "left"}
                            width={col.width ?? "auto"}
                            sx={BODY_CELL_SX}
                        >
                            <Box
                                sx={{
                                    ...getCellInnerSx({
                                        colIdx,
                                        totalColSpan,
                                        isGrouped: true,
                                        hasCheckbox
                                    }),
                                    justifyContent: colFlexAlign(col)
                                }}
                            >
                                {col.render(row, rowIndex)}
                            </Box>
                        </TableCell>
                    );
                })}
            </StyledTableRow>
            {isExpanded && itemBlock}
        </Fragment>
    );
}

export const GroupRow = memo(GroupRowImpl) as <TRow>(props: GroupRowProps<TRow>) => JSX.Element;
